import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal, ModalProps } from 'react-bootstrap';
import { createUseThemedStyles, useDebouncedState, useHandleError } from '@/hooks';
import { Loader } from '@/components';
import { aiConversationService } from '@/lib/services';
import { AiConversationListItem, AiConversationSections } from '@/lib/models';

const AiSearchModalStyles = createUseThemedStyles((theme) => ({
	dialog: {
		width: '90%',
		height: '100%',
		maxWidth: 620,
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			minHeight: 426,
			overflowY: 'auto',
			padding: '30px 20px',
		},
	},
	searchInput: {
		border: '0 !important',
		height: 'auto !important',
		textIndent: '0 !important',
		boxShadow: 'none !important',
	},
	searchResultButton: {
		border: 0,
		width: '100%',
		borderRadius: 4,
		display: 'block',
		textAlign: 'left',
		...theme.fonts.xs,
		appearance: 'none',
		padding: '8px 10px',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: theme.colors.gray100,
		},
		'&:active': {
			backgroundColor: theme.colors.gray200,
		},
	},
}));

interface AiSearchModalProps extends ModalProps {
	onAiConversationClick(aiConversation: AiConversationListItem): void;
}

export const AiSearchModal = ({ onAiConversationClick, ...props }: AiSearchModalProps) => {
	const classes = AiSearchModalStyles();
	const handleError = useHandleError();
	const searchInputRef = useRef<HTMLInputElement>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [searchInputValue, setSearchInputValue] = useState('');
	const [searchResults, setSearchResults] = useState<AiConversationSections>({});
	const debouncedSearchValue = useDebouncedState(searchInputValue);

	const handleOnEnter = useCallback(() => {
		searchInputRef.current?.focus();
	}, []);

	const handleOnExit = useCallback(() => {
		setSearchInputValue('');
		setSearchResults({});
	}, []);

	const fetchConversations = useCallback(async () => {
		setIsLoading(true);

		try {
			const response = await aiConversationService.getPastConversations({ query: debouncedSearchValue }).fetch();
			setSearchResults(response.aiConversations);
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [debouncedSearchValue, handleError]);

	useEffect(() => {
		if (!props.show) {
			return;
		}

		fetchConversations();
	}, [fetchConversations, props.show]);

	return (
		<Modal dialogClassName={classes.dialog} centered onEntering={handleOnEnter} onExited={handleOnExit} {...props}>
			<Modal.Header closeButton>
				<Form.Control
					ref={searchInputRef}
					type="search"
					placeholder="Search chats..."
					className={classes.searchInput}
					value={searchInputValue}
					onChange={({ currentTarget }) => {
						setSearchInputValue(currentTarget.value);
					}}
				/>
			</Modal.Header>
			<Modal.Body>
				{isLoading ? (
					<Loader />
				) : (
					<>
						{Object.keys(searchResults).length > 0 ? (
							<>
								{Object.entries(searchResults ?? {}).map(([key, value]) => (
									<section key={key}>
										<h6 className="mb-2 px-2 text-primary">{key}</h6>
										<div className="mb-4">
											{value.map((c) => (
												<div key={c.aiConversationId}>
													<button
														className={classes.searchResultButton}
														onClick={() => {
															onAiConversationClick(c);
														}}
													>
														{c.title}
													</button>
												</div>
											))}
										</div>
									</section>
								))}
							</>
						) : (
							<p className="m-0 px-2 small text-muted">No results</p>
						)}
					</>
				)}
			</Modal.Body>
		</Modal>
	);
};
